/**
 * Admin UI helpers — shared, presentation-only.
 *
 * This module contains ZERO API calls, ZERO write logic, and no state that
 * affects backend data. It is the visual grammar of the redesigned /admin
 * and /admin/suppliers screens:
 *   - Truth badges (GovListed, CorridorVerified, Claimed, Demo, ReviewCandidate,
 *     Evidence)
 *   - HelpTip / ActionTooltip (plain-English hovers on every action)
 *   - RuntimeSchemaMismatch (safety banner if backfilled fields are unreadable)
 *   - DataQualityPanel (read-only counters, informational only)
 *   - NextStepsPanel (numbered daily workflow)
 *   - AdvancedToolsSection (collapsible, red UNSAFE label, wraps existing
 *     dangerous buttons without changing what they do)
 *   - SummaryStrip (Total profiles / Public listings / Government-listed IMMEX
 *     plants / Company Claimed / Corridor Verified / Unverified / Demo /
 *     Duplicate Review Candidates)
 *   - EntityGroupList / FlatSupplierList (grouped-by-legal-entity default with
 *     a flat-list toggle; every plant remains its own row)
 *
 * IMPORTANT: no component here changes production data, no component here
 * calls IndexNow or sends email. Advanced buttons are re-labelled and
 * re-grouped but they still call the SAME existing handlers passed in as
 * props — the actual write behavior is unchanged.
 */
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Info, AlertTriangle, ShieldCheck, ShieldAlert, ChevronDown, ChevronRight,
  Building2, MapPin, FileText, ExternalLink, AlertOctagon, HelpCircle,
  CheckCircle2, XCircle, CircleSlash, Clock,
} from "lucide-react";

// The six duplicate-candidate plant_location_ids from ITER47 §4.
// Slugs are lowercased for comparison; production live rows never have their
// plant_location_id set, which is how "structured fields not backfilled"
// distinguishes them from the 630 completed rows.
export const DUPLICATE_CANDIDATE_SLUGS = new Set([
  "immex-2006-797-p03", "immex-2006-797-p04",
  "immex-2006-4118-p01", "immex-2006-4118-p02",
  "immex-2006-807-p06", "immex-2006-807-p07",
]);

/* ────────────────────────────────────────────────────────────────────── *
 * TRUTH BADGES
 * Every badge is a static label — no click handler, no state change.
 * ────────────────────────────────────────────────────────────────────── */

function BadgeShell({ children, tone = "neutral", title, testid }) {
  const tones = {
    gov:      { bg: "#f1f5f9", color: "#1e293b", border: "#cbd5e1" },   // slate
    verified: { bg: "#dcfce7", color: "#166534", border: "#86efac" },   // green
    claimed:  { bg: "#dbeafe", color: "#1e40af", border: "#93c5fd" },   // blue
    demo:     { bg: "#fef9c3", color: "#854d0e", border: "#fde68a" },   // yellow
    review:   { bg: "#ffedd5", color: "#9a3412", border: "#fdba74" },   // orange
    unverified: { bg: "#f8fafc", color: "#64748b", border: "#e2e8f0" }, // muted
    pending:  { bg: "#fef3c7", color: "#92400e", border: "#fcd34d" },   // amber
    evidence: { bg: "#ccfbf1", color: "#115e59", border: "#5eead4" },   // teal
    danger:   { bg: "#fee2e2", color: "#991b1b", border: "#fca5a5" },   // red
    neutral:  { bg: "transparent", color: "#475569", border: "#e2e8f0" },
  };
  const t = tones[tone] || tones.neutral;
  return (
    <span
      title={title}
      data-testid={testid}
      className="inline-flex items-center gap-1 font-mono"
      style={{
        fontSize: 10,
        lineHeight: 1,
        padding: "3px 6px",
        background: t.bg,
        color: t.color,
        border: `1px solid ${t.border}`,
        borderRadius: 2,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

/** Government-listed IMMEX — official government listing. Neutral slate. */
export function GovListedBadge({ testid = "badge-gov-listed", lang = "en" }) {
  return (
    <BadgeShell
      tone="gov"
      title="This record comes from the official Mexican government IMMEX listing. Corridor verification status is shown separately."
      testid={testid}
    >
      <FileText size={9} strokeWidth={2} />{" "}
      {lang === "es" ? "IMMEX listado por gobierno" : "Government-listed IMMEX"}
    </BadgeShell>
  );
}
/** Corridor verification pending — replaces every "Not Corridor Verified" wording. */
export function PendingVerificationBadge({ testid = "badge-verification-pending", lang = "en" }) {
  return (
    <BadgeShell
      tone="pending"
      title="Plant-specific Corridor verification is pending. Corridor Verified is granted only through the Compliance Vault evidence ceremony."
      testid={testid}
    >
      <Clock size={9} strokeWidth={2} />{" "}
      {lang === "es" ? "Verificación Corridor pendiente" : "Corridor verification pending"}
    </BadgeShell>
  );
}
/** Evidence-backed capability — teal, never mistakable for Corridor Verified green. */
export function EvidenceBackedBadge({ testid = "badge-evidence-backed", lang = "en" }) {
  return (
    <BadgeShell
      tone="evidence"
      title="One or more capabilities on this plant carry cited research evidence: a source URL and exact supporting text tied to this specific plant. This is not Corridor Verified."
      testid={testid}
    >
      <FileText size={9} strokeWidth={2} />{" "}
      {lang === "es" ? "Capacidad respaldada por evidencia" : "Evidence-backed capability"}
    </BadgeShell>
  );
}
export function CorridorVerifiedBadge({ testid = "badge-corridor-verified" }) {
  return (
    <BadgeShell
      tone="verified"
      title="A Corridor reviewer has confirmed plant-level evidence for this record."
      testid={testid}
    >
      <ShieldCheck size={9} strokeWidth={2} /> Corridor Verified
    </BadgeShell>
  );
}
export function LegacyVerifiedBadge({ testid = "badge-legacy-verified" }) {
  return (
    <BadgeShell
      tone="review"
      title="Legacy verified flag — carried over before the evidence-based verification ceremony existed. Needs plant-level evidence or an audited revoke."
      testid={testid}
    >
      <AlertTriangle size={9} strokeWidth={2} /> Legacy Verified — review
    </BadgeShell>
  );
}
export function ClaimedBadge({ testid = "badge-claimed" }) {
  return (
    <BadgeShell
      tone="claimed"
      title="A real user account claims ownership of this record."
      testid={testid}
    >
      <Building2 size={9} strokeWidth={2} /> Company Claimed
    </BadgeShell>
  );
}
export function UnclaimedBadge({ testid = "badge-unclaimed" }) {
  return (
    <BadgeShell
      tone="unverified"
      title="No company account has claimed this record yet."
      testid={testid}
    >
      Unclaimed
    </BadgeShell>
  );
}
export function DemoBadge({ testid = "badge-demo" }) {
  return (
    <BadgeShell
      tone="demo"
      title="Demo / test / seed record used for showcase or QA. Not a real supplier."
      testid={testid}
    >
      Demo/Test
    </BadgeShell>
  );
}
export function ReviewCandidateBadge({ testid = "badge-review-candidate" }) {
  return (
    <BadgeShell
      tone="review"
      title="Flagged as a potential duplicate of another record. Kept unchanged pending a manual reviewer decision. No merge, delete, or verification will happen automatically."
      testid={testid}
    >
      <AlertOctagon size={9} strokeWidth={2} /> Review Candidate
    </BadgeShell>
  );
}

/** Compact evidence-status pill for the table. */
export function EvidenceBadge({ evidence_state, testid }) {
  if (!evidence_state) {
    return (
      <BadgeShell
        tone="neutral"
        title="No structured evidence field on this record yet."
        testid={testid || "badge-evidence-none"}
      >
        <CircleSlash size={9} strokeWidth={2} /> No evidence
      </BadgeShell>
    );
  }
  const label = ({
    government_listed:   "Government-listed only",
    corridor_review:     "In corridor review",
    plant_level_proof:   "Plant-level proof on file",
    manually_overridden: "Manual override",
  })[evidence_state] || evidence_state;
  const tone = evidence_state === "plant_level_proof" ? "verified"
             : evidence_state === "manually_overridden" ? "review"
             : "gov";
  return (
    <BadgeShell
      tone={tone}
      title={`Evidence state: ${evidence_state}. This tells you where the trust for this record comes from.`}
      testid={testid || `badge-evidence-${evidence_state}`}
    >
      {label}
    </BadgeShell>
  );
}

/* ────────────────────────────────────────────────────────────────────── *
 * HELP TIPS
 * A HelpTip is a small info icon that reveals a plain-English explanation
 * on hover / focus. An ActionTooltip is the richer version used on every
 * action button: what it does, does it change data, does it send email or
 * IndexNow, can it be undone, is a backup required.
 * ────────────────────────────────────────────────────────────────────── */

export function HelpTip({ text, testid, size = 12 }) {
  return (
    <span
      className="inline-flex align-middle"
      style={{ marginLeft: 6, color: "var(--ink-2)", cursor: "help" }}
      tabIndex={0}
      title={text}
      data-testid={testid || "help-tip"}
    >
      <Info size={size} strokeWidth={1.6} />
    </span>
  );
}

/**
 * Render as: <ActionTooltip {...tooltipProps} />
 * All flags default to false so the shortest correct call is
 *   <ActionTooltip what="..." />
 * and everything else is opt-in.
 */
export function ActionTooltip({
  what,
  changesData = false,
  sendsEmail = false,
  sendsIndexNow = false,
  undoable = false,
  backupRequired = false,
  testid,
}) {
  // Rows only appear when their answer is non-neutral. That keeps the
  // tooltip readable — the reviewer sees only the risks that apply.
  const rows = [
    ["Changes data?",       changesData    ? "YES — writes to production" : "No"],
    ["Sends email?",        sendsEmail     ? "YES" : "No"],
    ["Sends IndexNow?",     sendsIndexNow  ? "YES — pings Bing/Yandex" : "No"],
    ["Can be undone?",      undoable       ? "Yes, by admin" : "Not automatically"],
    ["Backup required?",    backupRequired ? "YES — take a fresh backup first" : "No"],
  ];
  const tone = (changesData || sendsEmail || sendsIndexNow || backupRequired)
    ? "danger" : "neutral";
  const bg   = tone === "danger" ? "#fef2f2" : "#f8fafc";
  const bd   = tone === "danger" ? "#fecaca" : "#e2e8f0";
  return (
    <div
      className="inline-block align-middle"
      style={{ position: "relative", marginLeft: 6 }}
      data-testid={testid || "action-tooltip"}
    >
      <details style={{ display: "inline-block" }}>
        <summary
          style={{
            listStyle: "none",
            cursor: "pointer",
            color: tone === "danger" ? "#dc2626" : "var(--ink-2)",
            display: "inline-flex",
            alignItems: "center",
          }}
        >
          <HelpCircle size={14} strokeWidth={1.6} />
        </summary>
        <div
          style={{
            position: "absolute", zIndex: 50, top: "100%", left: 0,
            marginTop: 6, minWidth: 300, maxWidth: 380,
            background: bg, border: `1px solid ${bd}`, borderRadius: 4,
            padding: 12, boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
          }}
        >
          <div className="text-xs" style={{ color: "var(--ink)", marginBottom: 8 }}>
            {what}
          </div>
          <table className="w-full font-mono" style={{ fontSize: 10.5 }}>
            <tbody>
              {rows.map(([k, v]) => (
                <tr key={k}>
                  <td style={{ color: "var(--ink-2)", padding: "2px 8px 2px 0", whiteSpace: "nowrap" }}>{k}</td>
                  <td style={{ color: v.startsWith("YES") ? "#991b1b" : "var(--ink)" }}>{v}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────── *
 * SAFETY BANNER — Runtime/schema mismatch
 * Shown when there are ≥ 100 immex-* slugs live but ZERO of them carry
 * a plant_location_id. That means the runtime is talking to a database
 * where the approved backfill has not been applied — do NOT re-import,
 * do NOT retry the backfill.
 * ────────────────────────────────────────────────────────────────────── */

export function RuntimeSchemaMismatch({ immexCount, backfilledCount }) {
  if (immexCount < 100) return null;              // nothing to compare
  if (backfilledCount > 0) return null;           // fields readable, no mismatch
  return (
    <div
      className="mt-6 cd-card"
      style={{
        borderLeft: "4px solid #dc2626",
        background: "#fef2f2",
        padding: 16,
      }}
      data-testid="admin-runtime-schema-mismatch"
    >
      <div className="flex items-start gap-3">
        <AlertOctagon size={20} strokeWidth={1.6} style={{ color: "#dc2626", marginTop: 2 }} />
        <div className="flex-1">
          <div className="overline" style={{ color: "#dc2626" }}>
            RUNTIME / SCHEMA MISMATCH · REVIEW REQUIRED
          </div>
          <p className="mt-2 text-sm">
            <strong>{immexCount.toLocaleString()}</strong> government-listed IMMEX slugs are visible,
            but <strong>0</strong> of them expose the backfilled plant fields
            (<code>plant_location_id</code>, <code>evidence_state</code>, …).
          </p>
          <p className="mt-2 text-sm">
            This runtime is either pointed at a different database than the one the
            approved backfill was written to, or the deployment has not been rolled
            out. <strong>Do not re-import and do not retry the backfill.</strong>{" "}
            Verify the deployment target first.
          </p>
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────── *
 * SUMMARY STRIP — 8 counters at the top of /admin
 * ────────────────────────────────────────────────────────────────────── */

function CountTile({ label, value, tip, accent = "var(--ink)", testid, sub }) {
  return (
    <div className="cd-card p-4" data-testid={testid}>
      <div className="overline flex items-center" style={{ color: "var(--ink-2)" }}>
        <span>{label}</span>
        <HelpTip text={tip} testid={`${testid}-tip`} />
      </div>
      <div className="font-display text-3xl mt-2 leading-none" style={{ color: accent }}>
        {value.toLocaleString()}
      </div>
      {sub ? (
        <div className="font-mono text-xs mt-1" style={{ color: "var(--ink-2)" }}>{sub}</div>
      ) : null}
    </div>
  );
}

/**
 * Compute the eight headline counters from the raw admin manufacturer list
 * (as returned by GET /api/admin/manufacturers). Uses only fields that
 * already exist — no new field is invented, no field is renamed.
 */
export function computeSummary(items) {
  const total     = items.length;
  const published = items.filter((r) => r.published).length;
  const immex     = items.filter((r) => (r.slug || "").startsWith("immex-")).length;
  const claimed   = items.filter((r) => r.claimed === true).length;
  const corridor  = items.filter((r) => r.corridor_verified === true).length;
  const legacyVer = items.filter((r) => r.verified === true).length;
  // Unverified = anything not Corridor Verified.  Legacy `verified=true`
  // still counts as "trust needs review" but not Corridor Verified.
  const unverified = total - corridor;
  const demo      = items.filter((r) => r.is_demo === true).length;
  // Duplicate candidates come from the ITER47 §4 frozen list — we ONLY
  // show the count of live records whose slug matches that list, so the
  // reviewer sees exactly what's on production.
  const dupCandidates = items.filter(
    (r) => DUPLICATE_CANDIDATE_SLUGS.has((r.slug || "").toLowerCase())
  ).length;
  const backfilled = items.filter((r) => r.plant_location_id).length;
  return {
    total, published, immex, claimed, corridor, legacyVer, unverified, demo,
    dupCandidates, backfilled,
  };
}

export function SummaryStrip({ items }) {
  const s = useMemo(() => computeSummary(items), [items]);
  return (
    <section className="mt-6" data-testid="admin-summary-strip">
      <div className="overline mb-2">PLANT DATABASE · SUMMARY</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <CountTile
          label="TOTAL PROFILES"
          value={s.total}
          tip="Every manufacturer profile in the database, including unclaimed IMMEX registry imports and demo rows."
          testid="summary-total"
        />
        <CountTile
          label="PUBLIC LISTINGS"
          value={s.published}
          tip={`Profiles marked published=true. ${s.total.toLocaleString()} total − ${(s.total - s.published).toLocaleString()} unpublished = ${s.published.toLocaleString()} listings visible in the public directory.`}
          testid="summary-public"
          accent="var(--green)"
        />
        <CountTile
          label="GOV-LISTED IMMEX PLANTS"
          value={s.immex}
          tip={`Records imported from the Mexican government IMMEX registry (slug begins with "immex-"). ${s.backfilled.toLocaleString()} carry the 34 structured plant fields; ${(s.immex - s.backfilled).toLocaleString()} are review candidates awaiting a manual decision.`}
          testid="summary-immex"
          accent="#3b82f6"
          sub={`${s.backfilled.toLocaleString()} with structured fields`}
        />
        <CountTile
          label="COMPANY CLAIMED"
          value={s.claimed}
          tip="Profiles where a real user account has completed the claim flow. These records are owned by the company."
          testid="summary-claimed"
          accent="var(--green)"
        />
        <CountTile
          label="CORRIDOR VERIFIED"
          value={s.corridor}
          tip="Records where a Corridor reviewer has confirmed plant-level evidence. Corridor Verified is separate from — and stricter than — the legacy Verified flag."
          testid="summary-corridor-verified"
          accent="var(--green)"
          sub={s.legacyVer > s.corridor ? `${(s.legacyVer - s.corridor).toLocaleString()} legacy verified — review needed` : null}
        />
        <CountTile
          label="UNVERIFIED"
          value={s.unverified}
          tip={`Total (${s.total.toLocaleString()}) − Corridor Verified (${s.corridor.toLocaleString()}) = ${s.unverified.toLocaleString()}. Legacy verified records are counted here until they earn evidence-based verification.`}
          testid="summary-unverified"
          accent="var(--ink-2)"
        />
        <CountTile
          label="DEMO / TEST"
          value={s.demo}
          tip="Seeded showcase / QA rows. Not real suppliers. Kept unpublished for public visibility."
          testid="summary-demo"
          accent="#eab308"
        />
        <CountTile
          label="DUPLICATE REVIEW CANDIDATES"
          value={s.dupCandidates}
          tip="Six IMMEX plants flagged as possible duplicates in the audit report (EES P03/P04, STRATTEC P01/P02, ELECTROLUX P06/P07). Kept unchanged — no merge, no delete, no verification will happen automatically. Awaiting a manual decision tied to plant_location_id."
          testid="summary-dup-candidates"
          accent="#ea580c"
        />
      </div>
    </section>
  );
}

/* ────────────────────────────────────────────────────────────────────── *
 * DATA QUALITY PANEL — read-only, informational only
 * ────────────────────────────────────────────────────────────────────── */

export function DataQualityPanel({ items }) {
  const q = useMemo(() => {
    const immex = items.filter((r) => (r.slug || "").startsWith("immex-"));
    const backfilled = immex.filter((r) => r.plant_location_id);
    const withAddress = backfilled.filter((r) => r.plant_address);
    const withCoords  = backfilled.filter((r) => r.latitude && r.longitude);
    const withPark    = backfilled.filter((r) => r.assigned_park);
    return {
      matched: backfilled.length,
      unmatched: immex.length - backfilled.length,
      ambiguous: 0,
      dupCandidates: items.filter(
        (r) => DUPLICATE_CANDIDATE_SLUGS.has((r.slug || "").toLowerCase())
      ).length,
      missingAddress: backfilled.length - withAddress.length,
      missingCoords:  backfilled.length - withCoords.length,
      missingPark:    backfilled.length - withPark.length,
    };
  }, [items]);
  return (
    <section className="mt-6 cd-card p-5" data-testid="admin-data-quality">
      <div className="overline flex items-center">
        <span>DATA QUALITY · READ-ONLY</span>
        <HelpTip text="These are informational counters computed from the current data. Nothing on this panel writes, verifies, publishes, or sends anything." />
      </div>
      <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 font-mono text-xs">
        <QCell label="Matched" value={q.matched} tone="ok" tip="IMMEX plants whose 34 structured fields are populated on this record." />
        <QCell label="Unmatched" value={q.unmatched} tone={q.unmatched ? "warn" : "ok"} tip="IMMEX plants without structured plant fields — including the six duplicate-candidate review rows." />
        <QCell label="Ambiguous" value={q.ambiguous} tone={q.ambiguous ? "warn" : "ok"} tip="Records where the audit could not match to exactly one live row. Zero after the audit." />
        <QCell label="Duplicate candidates" value={q.dupCandidates} tone={q.dupCandidates ? "warn" : "ok"} tip="Six review candidates preserved unchanged pending a manual decision." />
        <QCell label="Missing address" value={q.missingAddress} tone={q.missingAddress ? "warn" : "ok"} tip="Backfilled plants without an exact street address." />
        <QCell label="Missing coordinates" value={q.missingCoords} tone={q.missingCoords ? "warn" : "ok"} tip="Backfilled plants with no latitude / longitude — mostly the government's Unmapped rows." />
        <QCell label="Missing park assignment" value={q.missingPark} tone={q.missingPark ? "warn" : "ok"} tip="Backfilled plants where the government address did not identify an IMIP-listed park." />
      </div>
    </section>
  );
}
function QCell({ label, value, tone, tip }) {
  const color = tone === "warn" ? "#9a3412" : "var(--ink-2)";
  const bg    = tone === "warn" ? "#fff7ed" : "transparent";
  return (
    <div className="border px-3 py-2" style={{ borderColor: "var(--line)", background: bg }} title={tip}>
      <div className="overline" style={{ color: "var(--ink-2)" }}>{label}</div>
      <div className="text-lg font-semibold mt-0.5" style={{ color }}>{(value ?? 0).toLocaleString()}</div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────── *
 * NEXT-STEPS PANEL — the safe daily workflow
 * ────────────────────────────────────────────────────────────────────── */

export function NextStepsPanel() {
  return (
    <section
      className="mt-6 cd-card"
      style={{ borderLeft: "4px solid var(--orange)", padding: 20 }}
      data-testid="admin-next-steps"
    >
      <div className="overline" style={{ color: "var(--orange)" }}>
        WHAT SHOULD I DO NEXT?
      </div>
      <p className="mt-2 text-sm" style={{ color: "var(--ink-2)" }}>
        The safe daily workflow. Read left → right. Every step below is
        reversible and never sends email or IndexNow.
      </p>
      <ol className="mt-4 space-y-3">
        <StepRow
          n={1}
          title="Review unmatched & duplicate candidates"
          body="Open the six flagged duplicate-candidate plants. Confirm whether each is a real second plant or a registry duplicate. Do not merge or delete — just take notes."
        />
        <StepRow
          n={2}
          title="Review evidence for one plant at a time"
          body="Open a plant profile → check the official government listing → compare with any documents in the Compliance Vault. This is a read-only investigation."
        />
        <StepRow
          n={3}
          title="Verify only after plant-specific proof"
          body="Use the new evidence-based verification ceremony (when it ships). Do NOT use the old one-click Verify — it is labelled Legacy and will be removed once the ceremony is live."
        />
        <StepRow
          n={4}
          title="Do NOT use Recompute Verified or Legacy CSV Import"
          body="These live under Advanced / Owner-only tools with clear warnings. They can change hundreds of records at once and are not part of the daily workflow."
          danger
        />
      </ol>
    </section>
  );
}
function StepRow({ n, title, body, danger }) {
  return (
    <li className="flex items-start gap-3" data-testid={`next-step-${n}`}>
      <span
        className="flex-shrink-0 flex items-center justify-center font-display"
        style={{
          width: 30, height: 30, borderRadius: 4,
          background: danger ? "#fee2e2" : "rgba(255,107,0,0.10)",
          color: danger ? "#991b1b" : "var(--orange)",
          fontSize: 15,
        }}
      >
        {n}
      </span>
      <div>
        <div className="text-sm font-semibold">{title}</div>
        <div className="text-sm mt-0.5" style={{ color: "var(--ink-2)" }}>{body}</div>
      </div>
    </li>
  );
}

/* ────────────────────────────────────────────────────────────────────── *
 * ADVANCED TOOLS SECTION — collapsed by default, red UNSAFE banner.
 * This is a wrapper only — buttons passed as children keep their existing
 * onClick handlers and existing testids. We do NOT change what they do.
 * ────────────────────────────────────────────────────────────────────── */

export function AdvancedToolsSection({ children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section
      className="mt-10 cd-card"
      style={{
        borderLeft: "4px solid #dc2626",
        background: "rgba(220,38,38,0.03)",
        padding: 0,
      }}
      data-testid="admin-advanced-tools"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          width: "100%", padding: "18px 20px", background: "transparent",
          border: 0, cursor: "pointer", textAlign: "left",
        }}
        data-testid="admin-advanced-tools-toggle"
      >
        <div className="flex items-center gap-3">
          <span
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "3px 8px", background: "#dc2626", color: "white",
              fontSize: 10, letterSpacing: 0.5, borderRadius: 2, fontWeight: 700,
            }}
          >
            <AlertOctagon size={11} strokeWidth={2.2} /> UNSAFE
          </span>
          <div>
            <div className="overline" style={{ color: "#dc2626" }}>
              ADVANCED / OWNER-ONLY TOOLS
            </div>
            <div className="text-sm mt-1" style={{ color: "var(--ink-2)" }}>
              Bulk / legacy actions that can rewrite many records at once.
              Read the tooltip on each one before clicking. Not part of the
              daily workflow.
            </div>
          </div>
        </div>
        {open ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
      </button>
      {open ? (
        <div style={{ padding: "0 20px 20px 20px" }}>{children}</div>
      ) : null}
    </section>
  );
}

export function AdvancedRow({ title, tooltipProps, action, resultLine }) {
  return (
    <div
      className="border-t"
      style={{ borderColor: "var(--line)", padding: "14px 0" }}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-start gap-2">
          <span
            style={{
              display: "inline-flex", alignItems: "center", padding: "2px 6px",
              background: "#fee2e2", color: "#991b1b", fontSize: 10,
              fontWeight: 700, letterSpacing: 0.5, borderRadius: 2,
            }}
          >
            UNSAFE
          </span>
          <div>
            <div className="text-sm font-semibold flex items-center">
              {title}
              <ActionTooltip {...tooltipProps} />
            </div>
            <div className="text-xs mt-0.5" style={{ color: "var(--ink-2)" }}>
              {tooltipProps.what}
            </div>
          </div>
        </div>
        <div>{action}</div>
      </div>
      {resultLine ? (
        <div className="mt-2 font-mono text-xs" style={{ color: "var(--ink-2)" }}>
          {resultLine}
        </div>
      ) : null}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────── *
 * SUPPLIER TABLE — grouped by legal entity by default, flat toggle.
 * Every plant remains its own row.  This component is presentation-only.
 * It renders whatever `items` are handed in — no filtering, no sorting,
 * no writes.
 * ────────────────────────────────────────────────────────────────────── */

/** Build (legal_entity_key -> array_of_plants), preserving input order. */
function groupByEntity(items) {
  const groups = new Map();
  for (const r of items) {
    // legal_entity_key is written by the backfill; for the 6 duplicate
    // candidates we fall back to a normalised company_name so they still
    // group with their siblings if the reviewer wants that.
    const key = (r.legal_entity_key
      || (r.legal_entity || r.company_name || "")
        .trim().toUpperCase().replace(/\s+/g, " ")) || "—";
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(r);
  }
  return Array.from(groups.entries());
}

/** Determine the badge set for one row. */
export function rowBadges(r) {
  const isDup   = DUPLICATE_CANDIDATE_SLUGS.has((r.slug || "").toLowerCase());
  const isImmex = (r.slug || "").startsWith("immex-");
  const badges = [];
  if (r.is_demo)                             badges.push("demo");
  if (isImmex && !r.corridor_verified)       badges.push("gov");
  if (r.corridor_verified === true)          badges.push("corridor");
  if (r.verified === true && !r.corridor_verified) badges.push("legacy");
  if (r.claimed === true)                    badges.push("claimed");
  else if (isImmex)                          badges.push("unclaimed");
  if (isDup)                                 badges.push("review");
  return badges;
}

function renderBadges(bs) {
  return bs.map((b, i) => {
    if (b === "gov")      return <GovListedBadge key={i} testid={`row-badge-gov-${i}`} />;
    if (b === "corridor") return <CorridorVerifiedBadge key={i} testid={`row-badge-corridor-${i}`} />;
    if (b === "legacy")   return <LegacyVerifiedBadge key={i} testid={`row-badge-legacy-${i}`} />;
    if (b === "claimed")  return <ClaimedBadge key={i} testid={`row-badge-claimed-${i}`} />;
    if (b === "unclaimed")return <UnclaimedBadge key={i} testid={`row-badge-unclaimed-${i}`} />;
    if (b === "demo")     return <DemoBadge key={i} testid={`row-badge-demo-${i}`} />;
    if (b === "review")   return <ReviewCandidateBadge key={i} testid={`row-badge-review-${i}`} />;
    return null;
  });
}

/**
 * The redesigned supplier table.  `mode` = "grouped" (default) or "flat".
 * `onLegacyVerify(row)` — kept as a prop so the OLD one-click toggle
 * remains functional but visibly labelled "Legacy Verify".  This does
 * not change what the underlying handler does.
 */
export function SupplierTable({
  items,
  mode = "grouped",
  onLegacyVerify,
  onLegacyFeatured,
}) {
  const grouped = useMemo(() => groupByEntity(items), [items]);

  if (mode === "flat") {
    return (
      <div className="cd-card p-0 overflow-x-auto" data-testid="supplier-table-flat">
        <TableHead />
        <table className="cd-table" style={{ width: "100%" }}>
          <tbody>
            {items.map((r) => (
              <TableRow
                key={r.profile_id}
                row={r}
                onLegacyVerify={onLegacyVerify}
                onLegacyFeatured={onLegacyFeatured}
              />
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="cd-card p-0 overflow-hidden" data-testid="supplier-table-grouped">
      <TableHead grouped />
      {grouped.map(([key, plants]) => (
        <EntityBlock
          key={key}
          entityKey={key}
          plants={plants}
          onLegacyVerify={onLegacyVerify}
          onLegacyFeatured={onLegacyFeatured}
        />
      ))}
    </div>
  );
}

function TableHead({ grouped }) {
  const cols = [
    grouped ? "" : "Company / Legal Entity",
    "Plants",
    "P#",
    "City",
    "Plant Address",
    "IMMEX Ref / Year",
    "Government Listing",
    "Claim",
    "Corridor Verified",
    "Evidence",
    "Actions",
  ];
  return (
    <div
      className="grid font-mono"
      style={{
        gridTemplateColumns: "220px 60px 50px 110px 1fr 110px 200px 110px 130px 160px 160px",
        gap: 0,
        fontSize: 10,
        color: "var(--ink-2)",
        letterSpacing: 0.5,
        borderBottom: "1px solid var(--line)",
        background: "#f8fafc",
        padding: "10px 12px",
        textTransform: "uppercase",
      }}
      data-testid="supplier-table-head"
    >
      {cols.map((c, i) => <div key={i}>{c}</div>)}
    </div>
  );
}

function EntityBlock({ entityKey, plants, onLegacyVerify, onLegacyFeatured }) {
  const [open, setOpen] = useState(true);
  const display = plants[0]?.legal_entity || plants[0]?.company_name || entityKey;
  const claimedCount  = plants.filter((p) => p.claimed).length;
  const verifiedCount = plants.filter((p) => p.corridor_verified).length;
  const hasReview     = plants.some((p) =>
    DUPLICATE_CANDIDATE_SLUGS.has((p.slug || "").toLowerCase()));
  return (
    <div data-testid={`entity-block-${entityKey}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          width: "100%", textAlign: "left", padding: "12px 12px",
          background: "#fdfdfd", border: 0, borderBottom: "1px solid var(--line)",
          cursor: "pointer", display: "flex", alignItems: "center", gap: 10,
        }}
        data-testid={`entity-toggle-${entityKey}`}
      >
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <Building2 size={14} strokeWidth={1.6} style={{ color: "var(--ink-2)" }} />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold truncate">{display}</div>
          <div className="font-mono text-xs" style={{ color: "var(--ink-2)" }}>
            {plants.length} {plants.length === 1 ? "plant location" : "plant locations"}
            {plants.length > 1 ? " · additional plant locations belong to the same IMMEX holder" : ""}
          </div>
        </div>
        {hasReview     ? <ReviewCandidateBadge /> : null}
        {verifiedCount ? <CorridorVerifiedBadge /> : null}
        {claimedCount  ? <ClaimedBadge /> : null}
      </button>
      {open ? (
        <table className="cd-table" style={{ width: "100%" }}>
          <tbody>
            {plants.map((p) => (
              <TableRow
                key={p.profile_id}
                row={p}
                onLegacyVerify={onLegacyVerify}
                onLegacyFeatured={onLegacyFeatured}
                grouped
              />
            ))}
          </tbody>
        </table>
      ) : null}
    </div>
  );
}

function TableRow({ row, onLegacyVerify, onLegacyFeatured, grouped }) {
  const isDup      = DUPLICATE_CANDIDATE_SLUGS.has((row.slug || "").toLowerCase());
  const isImmex    = (row.slug || "").startsWith("immex-");
  const backfilled = !!row.plant_location_id;
  const govUrl     = row.government_source_url;
  const badges     = rowBadges(row);
  const address    = row.plant_address || row.address || "—";
  const immexRef   = row.immex_program_reference || row.immex_number || "—";
  const immexYear  = row.immex_program_year;
  return (
    <tr
      data-testid={`supplier-row-${row.slug || row.profile_id}`}
      style={isDup ? { background: "#fff7ed" } : {}}
    >
      <td className="font-semibold" style={{ paddingLeft: grouped ? 32 : 12, maxWidth: 220 }}>
        <div className="truncate">{row.display_name || row.company_name || "—"}</div>
        <div className="font-mono text-xs" style={{ color: "var(--ink-2)" }}>
          {row.slug || "—"}
        </div>
      </td>
      <td className="font-mono text-xs">
        {row.plant_count_for_legal_entity ?? (grouped ? "" : "—")}
      </td>
      <td className="font-mono text-xs">
        {row.plant_sequence_label
          ? <span style={{ padding: "1px 5px", background: "#f1f5f9", borderRadius: 2 }}>{row.plant_sequence_label}</span>
          : "—"}
      </td>
      <td className="font-mono text-xs">{row.city || "—"}</td>
      <td className="font-mono text-xs" style={{ maxWidth: 320 }}>
        <div className="truncate" title={address}>{address}</div>
        {row.postal_code || row.colonia ? (
          <div className="text-xs" style={{ color: "var(--ink-2)" }}>
            {row.colonia ? row.colonia + " · " : ""}{row.postal_code || ""}
          </div>
        ) : null}
      </td>
      <td className="font-mono text-xs">
        {immexRef}
        {immexYear ? <span style={{ color: "var(--ink-2)" }}> · {immexYear}</span> : null}
      </td>
      <td>
        {isImmex ? <GovListedBadge /> : <span className="font-mono text-xs" style={{ color: "var(--ink-2)" }}>—</span>}
        {govUrl ? (
          <a
            href={govUrl} target="_blank" rel="noreferrer"
            title="Open the government IMMEX source document (opens in a new tab, read-only)."
            style={{ marginLeft: 4, color: "var(--ink-2)" }}
            data-testid={`row-gov-link-${row.slug}`}
          >
            <ExternalLink size={11} strokeWidth={1.6} />
          </a>
        ) : null}
      </td>
      <td>{row.claimed ? <ClaimedBadge /> : <UnclaimedBadge />}</td>
      <td>
        {row.corridor_verified
          ? <CorridorVerifiedBadge />
          : row.verified
            ? <LegacyVerifiedBadge />
            : <span className="font-mono text-xs" style={{ color: "var(--ink-2)" }}>Not verified</span>}
      </td>
      <td>
        {isDup && !backfilled ? (
          <span
            className="font-mono text-xs"
            title="Structured fields not backfilled. Manual review pending — do not merge or delete."
            style={{ color: "#9a3412" }}
            data-testid={`row-evidence-pending-${row.slug}`}
          >
            Manual review pending
          </span>
        ) : (
          <EvidenceBadge evidence_state={row.evidence_state} testid={`row-evidence-${row.slug}`} />
        )}
      </td>
      <td>
        <div className="flex flex-wrap items-center gap-1">
          <Link
            to={`/m/${row.slug}`}
            target="_blank"
            rel="noreferrer"
            className="btn btn-ghost"
            style={{ padding: "3px 8px", fontSize: 11, minHeight: 0 }}
            title="Open the public plant profile in a new tab. Read-only."
            data-testid={`row-open-${row.slug}`}
          >
            <MapPin size={11} strokeWidth={1.8} />
            <span style={{ marginLeft: 4 }}>Open</span>
          </Link>
          {onLegacyFeatured ? (
            <button
              type="button"
              onClick={() => onLegacyFeatured(row)}
              className="btn"
              style={{
                padding: "3px 8px", fontSize: 11, minHeight: 0,
                background: row.featured ? "var(--orange)" : "transparent",
                color: row.featured ? "white" : "var(--ink-2)",
                border: "1px solid " + (row.featured ? "var(--orange)" : "var(--line)"),
              }}
              title="Legacy Featured toggle. Marks/unmarks the record as Featured in the public directory. Does not send email or IndexNow. Reversible."
              data-testid={`row-legacy-featured-${row.slug}`}
            >
              {row.featured ? "★ Featured" : "Feature"}
            </button>
          ) : null}
          {onLegacyVerify ? (
            <Link
              to={`/admin/vault?plant=${row.slug}`}
              className="btn"
              style={{
                padding: "3px 8px", fontSize: 11, minHeight: 0,
                background: "transparent",
                color: "var(--green, #166534)",
                border: "1px solid var(--line)",
              }}
              title="Corridor Verified requires the evidence-based ceremony: upload plant-linked evidence in the Compliance Vault and complete the review. The old one-click Verify is hard-blocked server-side."
              data-testid={`row-verify-ceremony-${row.slug}`}
            >
              <ShieldCheck size={11} strokeWidth={1.8} />
              <span style={{ marginLeft: 4 }}>Verify · ceremony</span>
            </Link>
          ) : null}
        </div>
      </td>
    </tr>
  );
}

/* ────────────────────────────────────────────────────────────────────── *
 * SUPPLIER TABLE SEARCH / FILTER / MODE TOGGLE — presentational shell.
 * ────────────────────────────────────────────────────────────────────── */

export function TableControls({
  mode, setMode, query, setQuery, statusFilter, setStatusFilter, count,
}) {
  return (
    <div className="mt-4 flex flex-wrap items-center gap-3" data-testid="supplier-table-controls">
      <div className="overline mr-auto">
        {count.toLocaleString()} PLANT RECORDS · GROUPED BY LEGAL ENTITY BY DEFAULT
      </div>
      <select
        value={statusFilter}
        onChange={(e) => setStatusFilter(e.target.value)}
        className="cd-input font-mono text-xs"
        style={{ maxWidth: 220, height: 34 }}
        data-testid="supplier-table-filter-status"
      >
        <option value="">All rows</option>
        <option value="immex">Government-listed IMMEX only</option>
        <option value="claimed">Claimed only</option>
        <option value="corridor_verified">Corridor Verified only</option>
        <option value="review">Review candidates only</option>
        <option value="demo">Demo/Test only</option>
      </select>
      <input
        type="text"
        placeholder="Search company, IMMEX, city, slug…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        className="cd-input font-mono text-xs"
        style={{ maxWidth: 260, height: 34 }}
        data-testid="supplier-table-search"
      />
      <div
        role="tablist"
        aria-label="Table mode"
        style={{
          display: "inline-flex", border: "1px solid var(--line)",
          borderRadius: 2, overflow: "hidden",
        }}
      >
        <button
          type="button"
          onClick={() => setMode("grouped")}
          data-testid="supplier-table-mode-grouped"
          style={{
            padding: "6px 12px", background: mode === "grouped" ? "var(--ink)" : "transparent",
            color: mode === "grouped" ? "white" : "var(--ink)",
            fontFamily: "var(--font-mono)", fontSize: 11, border: 0, cursor: "pointer",
          }}
        >
          Grouped
        </button>
        <button
          type="button"
          onClick={() => setMode("flat")}
          data-testid="supplier-table-mode-flat"
          style={{
            padding: "6px 12px", background: mode === "flat" ? "var(--ink)" : "transparent",
            color: mode === "flat" ? "white" : "var(--ink)",
            fontFamily: "var(--font-mono)", fontSize: 11, border: 0, cursor: "pointer",
          }}
        >
          Flat list
        </button>
      </div>
    </div>
  );
}

export function applyTableFilters(items, { query, statusFilter }) {
  const q = query.trim().toLowerCase();
  return items.filter((r) => {
    if (statusFilter === "immex" && !(r.slug || "").startsWith("immex-")) return false;
    if (statusFilter === "claimed" && !r.claimed) return false;
    if (statusFilter === "corridor_verified" && !r.corridor_verified) return false;
    if (statusFilter === "review" && !DUPLICATE_CANDIDATE_SLUGS.has((r.slug || "").toLowerCase())) return false;
    if (statusFilter === "demo" && !r.is_demo) return false;
    if (!q) return true;
    return [
      r.company_name, r.legal_entity, r.display_name,
      r.slug, r.city, r.immex_number, r.immex_program_reference,
      r.plant_location_id, r.plant_address,
    ].some((v) => (v || "").toString().toLowerCase().includes(q));
  });
}
